import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, ImageIcon } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { photoSrc, toISO, money } from "@/lib/api";
import { useStayPicker } from "@/hooks/useAvailability";
import { DateRangeSelect, GuestSelect } from "@/components/site/StayPicker";
import { SampleBadge } from "@/components/site/SampleBadge";

const HeroImage = ({ photo }) =>
  photo ? (
    <img src={photoSrc(photo.url)} alt={photo.caption || "Studio flat interior"} className="w-full h-full object-cover" data-testid="hero-image" />
  ) : (
    <div className="w-full h-full bg-secondary flex items-center justify-center" data-testid="hero-placeholder"><ImageIcon className="w-8 h-8 opacity-40" /></div>
  );

export const Hero = () => {
  const { site } = useSite();
  const nav = useNavigate();
  const picker = useStayPicker(site.pricing.min_nights);
  const range = picker.range;
  const [guests, setGuests] = useState(1);
  const hero = site?.photos?.find((p) => p.is_hero) || site?.photos?.[0];
  const go = () => {
    const q = new URLSearchParams({ g: guests });
    if (range?.from) q.set("ci", toISO(range.from));
    if (range?.to) q.set("co", toISO(range.to));
    nav(`/book?${q}`);
  };
  return (
    <section id="top" className="px-5 sm:px-8 lg:px-12 pt-10 sm:pt-16 pb-16" data-testid="hero-section">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-6 fade-up">
          <p className="eyebrow flex items-center gap-2" data-testid="hero-location"><MapPin className="w-3.5 h-3.5" /> Beaumont, Texas</p>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] font-medium">Your Cozy Home Away From Home in Beaumont</h1>
          <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-xl leading-relaxed">Settle into a comfortable, private studio flat with a full kitchen, cozy queen bed, and convenient access to St. Elizabeth Hospital, local coffee shops, restaurants, and major highways.</p>
          {!site.bookings_active ? (
            <div className="mt-8 border border-border bg-[hsl(var(--cream))] rounded-lg p-5 text-sm leading-relaxed" data-testid="hero-bookings-paused">{site.pause_message}</div>
          ) : (
          <div className="mt-8 border border-border bg-[hsl(var(--cream))] rounded-lg p-4 sm:p-5" data-testid="hero-booking-widget">
            <div className="grid sm:grid-cols-[1fr_150px] gap-3 items-start">
              <DateRangeSelect picker={picker} />
              <GuestSelect guests={guests} setGuests={setGuests} testid="hero-guest-select" />
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm" data-testid="hero-rate"><strong>{money(site.pricing.nightly_rate)}</strong> / night <SampleBadge /> <span className="block text-xs text-muted-foreground">Minimum stay: {site.pricing.min_nights} nights</span></p>
              <button onClick={go} className="bg-primary text-primary-foreground px-7 py-3 rounded-full text-sm font-semibold hover:bg-[hsl(var(--sage))] transition-colors" data-testid="hero-check-availability-button">Check Availability</button>
            </div>
          </div>
          )}
          <p className="mt-3 text-xs text-muted-foreground">Book directly with the host. Secure payment by Stripe.</p>
        </div>
        <div className="lg:col-span-6 aspect-square rounded-lg overflow-hidden border border-border fade-up" style={{ animationDelay: ".15s" }}>
          <HeroImage photo={hero} />
        </div>
      </div>
    </section>
  );
};
